<template>
  <aside class="filter-panel" aria-label="Experiment filters">
    <div class="panel-header">
      <h4>Filters<HelpLink topic="finding-experiments" title="How filters work" /></h4>
      <button type="button" class="link-button" :disabled="loading" @click="$emit('clear')">
        Clear all
      </button>
    </div>

    <form @submit.prevent="apply">
      <details class="filter-section">
        <summary>
          Project
          <span v-if="modelValue.projects.length" class="badge">{{ modelValue.projects.length }}</span>
        </summary>
        <multiselect
          id="filter-projects"
          :model-value="modelValue.projects"
          :options="projects"
          :multiple="true"
          :close-on-select="false"
          label="name"
          track-by="id"
          placeholder="Any project"
          aria-label="Project"
          @update:model-value="update('projects', $event)"
        />
      </details>

      <details class="filter-section">
        <summary>
          Location
          <span v-if="modelValue.locations.length" class="badge">{{ modelValue.locations.length }}</span>
        </summary>
        <multiselect
          id="filter-locations"
          :model-value="modelValue.locations"
          :options="options.locations || []"
          :multiple="true"
          :close-on-select="false"
          label="name"
          track-by="id"
          placeholder="Any location"
          aria-label="Location"
          @update:model-value="update('locations', $event)"
        />
      </details>

      <details class="filter-section">
        <summary>
          Instrument
          <span v-if="modelValue.instruments.length" class="badge">{{ modelValue.instruments.length }}</span>
        </summary>
        <multiselect
          id="filter-instruments"
          :model-value="modelValue.instruments"
          :options="options.instruments || []"
          :multiple="true"
          :close-on-select="false"
          :custom-label="serialLabel"
          track-by="id"
          placeholder="Any instrument"
          aria-label="Instrument"
          @update:model-value="update('instruments', $event)"
        />
      </details>

      <details class="filter-section">
        <summary>
          Linked materials
          <span v-if="materialCount" class="badge">{{ materialCount }}</span>
        </summary>
        <label class="field-label" for="filter-materials">Specific materials (optional)</label>
        <multiselect
          id="filter-materials"
          :model-value="modelValue.materials"
          :options="options.materials || []"
          :multiple="true"
          :close-on-select="false"
          :custom-label="batchLabel"
          track-by="id"
          :placeholder="modelValue.materialMetadata.length ? 'Any directly used material' : 'Any material'"
          aria-label="Material"
          @update:model-value="onMaterialsChange"
        />
        <template v-if="modelValue.materials.length">
          <label class="field-label" for="filter-relation-types">Relation to experiment</label>
          <multiselect
            id="filter-relation-types"
            :model-value="modelValue.relationTypes"
            :options="options.material_relation_types || []"
            :multiple="true"
            :close-on-select="false"
            label="name"
            track-by="id"
            placeholder="Any relation"
            @update:model-value="update('relationTypes', $event)"
          />
        </template>
        <div class="material-conditions">
          <h5>Material metadata</h5>
          <p class="hint">Uses current material values.</p>
          <p v-if="modelValue.materialMetadata.length" class="matching-note">
            At least one directly used material must match every condition below.
            If specific materials are selected, that same material must be one of them.
          </p>
          <p v-if="modelValue.materialMetadata.length && modelValue.relationTypes.length" class="hint">
            The relation filter is an additional constraint; material metadata still only matches
            direct uses_material links.
          </p>
          <MetadataFilterEditor
            id-prefix="filter-material-metadata"
            scope="material"
            :lists="options.material_metadata_lists || []"
            :selected-lists="modelValue.materialMetadataLists"
            :rows="modelValue.materialMetadata"
            @change="onMetadataChange('material', $event)"
          />
        </div>
      </details>

      <details class="filter-section">
        <summary>
          Date range
          <span v-if="dateCount" class="badge">{{ dateCount }}</span>
        </summary>
        <div class="date-row">
          <div>
            <label class="field-label" for="filter-start-date">From</label>
            <input
              id="filter-start-date"
              type="date"
              :value="modelValue.startDate"
              :max="modelValue.endDate || null"
              @input="update('startDate', $event.target.value)"
            />
          </div>
          <div>
            <label class="field-label" for="filter-end-date">To</label>
            <input
              id="filter-end-date"
              type="date"
              :value="modelValue.endDate"
              :min="modelValue.startDate || null"
              @input="update('endDate', $event.target.value)"
            />
          </div>
        </div>
      </details>

      <details class="filter-section">
        <summary>
          Experiment metadata
          <span v-if="modelValue.metadata.length" class="badge">{{ modelValue.metadata.length }}</span>
        </summary>
        <MetadataFilterEditor
          id-prefix="filter-experiment-metadata"
          scope="experiment"
          :lists="options.metadata_lists || []"
          :selected-lists="modelValue.metadataLists"
          :rows="modelValue.metadata"
          @change="onMetadataChange('experiment', $event)"
        />
      </details>

      <div class="panel-footer">
        <button type="submit" class="apply-button" :disabled="loading || hasErrors">
          Apply filters
        </button>
      </div>
    </form>
  </aside>
</template>

<script>
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';
import MetadataFilterEditor from '@/components/MetadataFilterEditor.vue';
import { validateMetadataRow } from '@/utils/experimentFilters.js';

export default {
  name: 'ExperimentFilterPanel',
  components: { Multiselect, MetadataFilterEditor },
  props: {
    modelValue: { type: Object, required: true },
    options: { type: Object, default: () => ({}) },
    projects: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'apply', 'clear'],
  computed: {
    materialCount() {
      return this.modelValue.materials.length + this.modelValue.materialMetadata.length;
    },
    dateCount() {
      return Number(Boolean(this.modelValue.startDate)) + Number(Boolean(this.modelValue.endDate));
    },
    hasErrors() {
      return [...this.modelValue.metadata, ...this.modelValue.materialMetadata]
        .some((row) => validateMetadataRow(row).status === 'invalid');
    },
  },
  methods: {
    apply() {
      if (!this.loading && !this.hasErrors) this.$emit('apply');
    },
    update(field, value) {
      this.$emit('update:modelValue', { ...this.modelValue, [field]: value });
    },
    onMaterialsChange(materials) {
      this.$emit('update:modelValue', {
        ...this.modelValue,
        materials,
        relationTypes: materials.length ? this.modelValue.relationTypes : [],
      });
    },
    onMetadataChange(scope, { rows, selectedLists }) {
      const field = scope === 'material' ? 'materialMetadata' : 'metadata';
      const listField = scope === 'material' ? 'materialMetadataLists' : 'metadataLists';
      this.$emit('update:modelValue', {
        ...this.modelValue,
        [field]: rows,
        [listField]: selectedLists,
      });
    },
    serialLabel(item) {
      return item.serial ? `${item.name} (${item.serial})` : item.name;
    },
    batchLabel(item) {
      return item.batch ? `${item.name} (${item.batch})` : item.name;
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/variables.scss";

.filter-panel {
  background: $form-color;
  border: 1px solid $secondary-color;
  border-radius: 8px;
  padding: 12px 16px 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;

  h4 {
    margin: 0;
    color: $primary-color;
  }
}

.badge {
  display: inline-block;
  min-width: 1.4em;
  margin-left: 6px;
  padding: 0 6px;
  border-radius: 999px;
  background: $primary-color;
  color: #fff;
  font-size: 0.75rem;
  text-align: center;
}

.filter-section {
  border-top: 1px solid $secondary-color;
  padding: 10px 0;

  summary {
    cursor: pointer;
    font-weight: 600;
    color: $primary-color;
    margin-bottom: 8px;

    &:focus-visible {
      outline: 2px solid $primary-color;
      outline-offset: 2px;
    }
  }
}

.field-label {
  display: block;
  margin: 8px 0 4px;
  font-size: 0.85rem;
}

input,
select {
  width: 100%;
  box-sizing: border-box;
  padding: 6px 8px;
  border: 1px solid #c8c8c0;
  border-radius: 5px;
  background: $form-color;

  &:focus-visible {
    outline: 2px solid $primary-color;
    outline-offset: 1px;
  }

  &[aria-invalid="true"] {
    border-color: #b3261e;
  }
}

.date-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.hint {
  font-size: 0.85rem;
  color: #555;
}

.material-conditions {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid $secondary-color;

  h5 { margin: 0; font-size: 0.95rem; }
}

.matching-note {
  padding: 8px;
  border-left: 3px solid $primary-color;
  background: $global-bg-color;
  font-size: 0.85rem;
}

.link-button {
  align-self: flex-start;
  padding: 2px 0;
  border: none;
  background: none;
  color: $primary-color;
  text-decoration: underline;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid $primary-color;
    outline-offset: 2px;
  }
}

.panel-footer {
  position: sticky;
  bottom: 0;
  padding-top: 10px;
  background: $form-color;
}

.apply-button {
  width: 100%;
  padding: 8px 16px;
  border: none;
  border-radius: 5px;
  background: $primary-color;
  color: #fff;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: darken($primary-color, 10%);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}
</style>
